import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useRef } from 'react';
import { createStore as createVanillaStore, useStore as useZustandStore } from 'zustand';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { selectors } from '~/store/state-selectors';
import { useJobsListener } from './hooks/useJobsListener';
export function createTasksDrawerStore() {
    return createVanillaStore((set)=>({
            isDrawerOpen: false,
            isTasksDrawerAvailable: false,
            awaitedJobs: new Map(),
            openDrawer: ()=>set({
                    isDrawerOpen: true
                }),
            closeDrawer: ()=>set({
                    isDrawerOpen: false
                }),
            toggle: ()=>set((s)=>({
                        isDrawerOpen: !s.isDrawerOpen
                    })),
            setTasksDrawerAvailable: (available)=>set({
                    isTasksDrawerAvailable: available
                }),
            registerAwaitedJob: (job)=>set((s)=>{
                    const awaitedJobs = new Map(s.awaitedJobs);
                    awaitedJobs.set(job.jobId, job);
                    return {
                        awaitedJobs
                    };
                }),
            dropAwaitedJob: (jobId)=>set((s)=>{
                    if (!s.awaitedJobs.has(jobId)) return s;
                    const awaitedJobs = new Map(s.awaitedJobs);
                    awaitedJobs.delete(jobId);
                    return {
                        awaitedJobs
                    };
                })
        }));
}
const TasksDrawerStoreContext = /*#__PURE__*/ createContext(null);
export function TasksDrawerStoreProvider(param) {
    let { children, store } = param;
    const value = useMemo(()=>store !== null && store !== void 0 ? store : createTasksDrawerStore(), [
        store
    ]);
    const { RELATIONSHIP_PDF_SCOUT } = useFeatureFlags();
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    return /*#__PURE__*/ _jsxs(TasksDrawerStoreContext.Provider, {
        value: value,
        children: [
            RELATIONSHIP_PDF_SCOUT && hasUserInfo && /*#__PURE__*/ _jsx(JobsListenerHost, {}),
            children
        ]
    });
}
function JobsListenerHost() {
    useJobsListener();
    return null;
}
export function useTasksDrawerStoreApi() {
    const contextStore = useContext(TasksDrawerStoreContext);
    const fallbackRef = useRef(null);
    if (contextStore != null) return contextStore;
    if (fallbackRef.current == null) fallbackRef.current = createTasksDrawerStore();
    return fallbackRef.current;
}
export function useTasksDrawer() {
    const store = useTasksDrawerStoreApi();
    const isDrawerOpen = useZustandStore(store, (s)=>s.isDrawerOpen);
    const isTasksDrawerAvailable = useZustandStore(store, (s)=>s.isTasksDrawerAvailable);
    const openDrawer = useZustandStore(store, (s)=>s.openDrawer);
    const closeDrawer = useZustandStore(store, (s)=>s.closeDrawer);
    const toggle = useZustandStore(store, (s)=>s.toggle);
    const setTasksDrawerAvailable = useZustandStore(store, (s)=>s.setTasksDrawerAvailable);
    return {
        isDrawerOpen,
        isTasksDrawerAvailable,
        openDrawer,
        closeDrawer,
        toggle,
        setTasksDrawerAvailable
    };
}
